import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { useLiveQuery } from 'dexie-react-hooks'

import { createBlockGrid, slotsPerHour, systemTimeZone, type BlockSlot } from '@/core/time/blockGrid'
import { addDays, dayKeyToISO } from '@/core/time/dayKey'
import { getSettings } from '@/core/repository/settingsRepository'
import { entriesForDayRange } from '@/core/repository/entryRepository'
import {
  formatDuration,
  selectionContains,
  selectionMinutes,
  selectionSlots,
} from '@/core/services/markingService'
import { DEFAULT_SETTINGS } from '@/core/types/settings'
import { labelColorOn } from '@/design/contrast'
import { patternFor } from '@/design/patterns'
import { applyAppearance, watchSystemScheme } from '@/design/appearance'
import { useNow } from '@/shared/hooks/useNow'
import { useKeyboardShortcuts } from '@/shared/hooks/useKeyboardShortcuts'
import { EventPicker } from '@/features/picker/EventPicker'
import { BlockDetail } from '@/features/detail/BlockDetail'
import { BackupPanel } from '@/features/backup/BackupPanel'
import { ManageScreen } from '@/features/manage/ManageScreen'

import { setCategoryCollapsed } from '@/core/repository/catalogRepository'
import { SyncPanel } from '@/features/sync/SyncPanel'
import { useSync } from '@/features/sync/useSync'
import { db } from '@/db/schema'
import type { CategoryRow, EntryRow } from '@/db/types'

import { BlockRow } from './BlockRow'
import { BlockContextMenu } from './BlockContextMenu'
import { MoreMenu } from './MoreMenu'
import { syncDot } from './syncDot'
import { NowMarker } from './NowMarker'
import { StatusToolbar } from './StatusToolbar'
import { blockLabel } from './blockLabel'
import { buildDayRuns } from './blockRunBuilder'
import { DIVIDER_HEIGHT, GUTTER_WIDTH, heightsFor, MIN_HEIGHT_FOR_LABEL } from './metrics'
import { buildTimelineWindow, offsetForInstant } from './timelineWindow'
import { useBlockGestures } from './useBlockGestures'
import { useTimelineActions } from './useTimelineActions'
import { slotKey, useTimelineStore } from './useTimelineStore'
import './timeline.css'

/**
 * Statistics and Trend are loaded on demand.
 *
 * They pull in Recharts, which is about 110 KB gzipped -- nearly as much as the
 * whole rest of the app. Bundling it into the initial download would make the
 * timeline slower to start for the sake of two screens that are opened
 * occasionally, on a phone, over a mobile connection. Splitting them keeps the
 * core loop at its previous size and costs a brief spinner the first time
 * either screen is opened. The service worker precaches both chunks, so that
 * spinner appears once and never offline.
 */
const StatisticsScreen = lazy(() =>
  import('@/features/analysis/StatisticsScreen').then((m) => ({ default: m.StatisticsScreen })),
)
const TrendScreen = lazy(() =>
  import('@/features/analysis/TrendScreen').then((m) => ({ default: m.TrendScreen })),
)

/**
 * Calendar, Goals and Stampers are lazily loaded too.
 *
 * Not for Recharts' sake -- none of them uses it -- but because they are all
 * occasional screens, and the rule is the same: the timeline should not carry
 * bytes it does not need to start.
 */
const CalendarScreen = lazy(() =>
  import('@/features/calendar/CalendarScreen').then((m) => ({ default: m.CalendarScreen })),
)
const GoalsScreen = lazy(() =>
  import('@/features/goals/GoalsScreen').then((m) => ({ default: m.GoalsScreen })),
)
const StampersScreen = lazy(() =>
  import('@/features/stampers/StampersScreen').then((m) => ({ default: m.StampersScreen })),
)
const SettingsScreen = lazy(() =>
  import('@/features/settings/SettingsScreen').then((m) => ({ default: m.SettingsScreen })),
)

/** Days either side of today. Roughly a year each way. */
const WINDOW_RADIUS_DAYS = 370

export function TimelineScreen() {
  const scrollRef = useRef<HTMLDivElement>(null)
  const didInitialScroll = useRef(false)

  const [showSync, setShowSync] = useState(false)
  const [showBackup, setShowBackup] = useState(false)
  const [showManage, setShowManage] = useState(false)
  const [showStats, setShowStats] = useState(false)
  const [showTrend, setShowTrend] = useState(false)
  const [showCalendar, setShowCalendar] = useState(false)
  const [showGoals, setShowGoals] = useState(false)
  const [showStampers, setShowStampers] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [showMore, setShowMore] = useState(false)
  const [detail, setDetail] = useState<{ slot: BlockSlot; focusComment: boolean } | null>(null)
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null)

  const settings = useLiveQuery(() => getSettings(), [], DEFAULT_SETTINGS)
  const now = useNow()

  const { status: syncStatus, lastSyncAt, syncNow, scheduleSync } = useSync(settings.syncEnabled)

  const selection = useTimelineStore((s) => s.selection)
  const sticky = useTimelineStore((s) => s.stickySelection)
  const clipboard = useTimelineStore((s) => s.clipboard)
  const undoDepth = useTimelineStore((s) => s.undoStack.length)
  const redoDepth = useTimelineStore((s) => s.redoStack.length)
  const selectSlot = useTimelineStore((s) => s.selectSlot)
  const extendSelection = useTimelineStore((s) => s.extendSelection)
  const setSticky = useTimelineStore((s) => s.setSticky)
  const visibleDayKey = useTimelineStore((s) => s.visibleDayKey)
  const setVisibleDayKey = useTimelineStore((s) => s.setVisibleDayKey)
  const hasUserScrolled = useTimelineStore((s) => s.hasUserScrolled)
  const markUserScrolled = useTimelineStore((s) => s.markUserScrolled)
  const justMarked = useTimelineStore((s) => s.justMarked)

  const timeZone = systemTimeZone()

  const grid = useMemo(
    () =>
      createBlockGrid({
        precisionMinutes: settings.precisionMinutes,
        dayStartHour: settings.dayStartHour,
        timeZone,
      }),
    [settings.precisionMinutes, settings.dayStartHour, timeZone],
  )

  const heights = useMemo(
    () => heightsFor(settings.precisionMinutes, settings.blockHeightScale),
    [settings.precisionMinutes, settings.blockHeightScale],
  )

  const anchorDayKey = useMemo(() => grid.dayKeyForInstant(Date.now()), [grid])

  const timelineWindow = useMemo(
    () =>
      buildTimelineWindow(
        grid,
        addDays(anchorDayKey, -WINDOW_RADIUS_DAYS),
        addDays(anchorDayKey, WINDOW_RADIUS_DAYS),
      ),
    [grid, anchorDayKey],
  )

  // eslint-disable-next-line react-hooks/incompatible-library
  const virtualizer = useVirtualizer({
    count: timelineWindow.totalRows,
    getScrollElement: () => scrollRef.current,
    estimateSize: (index) => timelineWindow.rowSize(index, heights),
    overscan: 12,
  })

  const virtualItems = virtualizer.getVirtualItems()

  const firstVisibleIndex = virtualItems[0]?.index ?? 0
  const lastVisibleIndex = virtualItems[virtualItems.length - 1]?.index ?? 0
  const loadLo = addDays(timelineWindow.dayAt(firstVisibleIndex)?.dayKey ?? anchorDayKey, -2)
  const loadHi = addDays(timelineWindow.dayAt(lastVisibleIndex)?.dayKey ?? anchorDayKey, 2)

  const entries = useLiveQuery(() => entriesForDayRange(loadLo, loadHi), [loadLo, loadHi], [])
  const eventTypes = useLiveQuery(() => db.eventTypes.toArray(), [], [])
  const eventObjects = useLiveQuery(() => db.eventObjects.toArray(), [], [])

  const objectNameById = useMemo(() => {
    const map = new Map<string, string>()
    for (const o of eventObjects) map.set(o.id, o.name)
    return map
  }, [eventObjects])

  const typeById = useMemo(() => {
    const map = new Map<
      string,
      { colorHex: string; shortcutText: string; name: string; pattern: string | null }
    >()
    for (const t of eventTypes) {
      map.set(t.id, {
        colorHex: t.colorHex,
        shortcutText: t.shortcutText,
        name: t.name,
        // Computed here, once per catalogue change, rather than per row per
        // frame -- the hue maths is cheap but there are ~40 rows on screen.
        pattern: patternFor(t.colorHex, labelColorOn(t.colorHex)),
      })
    }
    return map
  }, [eventTypes])

  const runsByDay = useMemo(() => {
    const byDay = new Map<number, EntryRow[]>()
    for (const entry of entries) {
      const list = byDay.get(entry.dayKey)
      if (list) list.push(entry)
      else byDay.set(entry.dayKey, [entry])
    }

    const out = new Map<number, ReturnType<typeof buildDayRuns>>()
    for (const [dayKey, dayEntries] of byDay) {
      out.set(dayKey, buildDayRuns(dayEntries, grid.slotsPerDay(dayKey)))
    }
    return out
  }, [entries, grid])

  const hourFormat = useMemo(
    () => new Intl.DateTimeFormat(undefined, { timeZone, hour: '2-digit', minute: '2-digit' }),
    [timeZone],
  )
  const dayFormat = useMemo(
    () =>
      new Intl.DateTimeFormat(undefined, {
        timeZone,
        weekday: 'short',
        day: 'numeric',
        month: 'short',
      }),
    [timeZone],
  )
  /** Short weekday: the long form crowds the buttons off a phone screen. */
  const titleFormat = useMemo(
    () =>
      new Intl.DateTimeFormat(undefined, {
        timeZone,
        weekday: 'short',
        day: 'numeric',
        month: 'long',
      }),
    [timeZone],
  )

  const perHour = slotsPerHour(grid)
  const nowOffset = offsetForInstant(timelineWindow, grid, now, heights)
  const nowIndex = timelineWindow.indexOf(grid.slotForInstant(now))
  const showLabels = heights.row >= MIN_HEIGHT_FOR_LABEL

  const selectedKeys = useMemo(() => {
    if (!selection) return new Set<string>()
    return new Set(selectionSlots(grid, selection).map(slotKey))
  }, [selection, grid])

  /** An event type can carry a default object, attached automatically on mark. */
  const defaultObjectFor = useCallback(
    (eventTypeId: string) => eventTypes.find((t) => t.id === eventTypeId)?.defaultObjectId ?? null,
    [eventTypes],
  )

  const actions = useTimelineActions(grid, {
    cursorMode: settings.cursorMode,
    skipOccupied: settings.skipOccupiedOnMultiMark,
    soundEnabled: settings.soundEnabled,
    defaultObjectFor,
    onChanged: scheduleSync,
  })

  // --- Gestures --------------------------------------------------------------

  /** Screen position to virtual row, via the window's own layout maths. */
  const rowAtClientY = useCallback(
    (clientY: number): number | null => {
      const el = scrollRef.current
      if (!el) return null
      const rect = el.getBoundingClientRect()
      const contentY = clientY - rect.top + el.scrollTop
      const index = timelineWindow.indexAtOffset(contentY, heights)
      return index >= 0 ? index : null
    },
    [timelineWindow, heights],
  )

  const selectByIndex = useCallback(
    (index: number) => {
      const slot = timelineWindow.slotAt(index)
      if (slot) selectSlot(slot)
    },
    [timelineWindow, selectSlot],
  )

  /**
   * Pressing inside an existing range must NOT collapse it.
   *
   * Otherwise long-pressing a block to reach Copy destroys the very selection
   * you were about to copy, and only that one block ends up on the clipboard.
   * Read from the store rather than a closure so this can never act on a stale
   * selection mid-gesture.
   */
  const selectUnlessInside = useCallback(
    (index: number) => {
      const slot = timelineWindow.slotAt(index)
      if (!slot) return

      const current = useTimelineStore.getState().selection
      if (current && selectionContains(grid, current, slot)) return

      selectSlot(slot)
    },
    [timelineWindow, grid, selectSlot],
  )

  const gestures = useBlockGestures(scrollRef, {
    rowAtClientY,
    onTap: selectByIndex,
    onDragStart: selectByIndex,
    onDragMove: (index) => {
      const slot = timelineWindow.slotAt(index)
      if (slot) extendSelection(slot)
    },
    onDragEnd: () => {
      // A range the user dragged out is meant to be marked as a unit, so hold
      // it rather than letting the cursor advance out from under them.
      setSticky(true)
    },
    onStickySelect: (index) => {
      selectUnlessInside(index)
      setSticky(true)
    },
    onContextMenu: (index, x, y) => {
      selectUnlessInside(index)
      setMenu({ x, y })
    },
  })

  // --- Scroll ---------------------------------------------------------------

  /**
   * Appearance follows settings, and is re-applied when the system flips
   * between light and dark.
   *
   * The watcher is only needed while `colorScheme` is 'system' -- with an
   * explicit choice the media query is irrelevant -- but subscribing
   * unconditionally is simpler than reasoning about when to detach, and the
   * listener costs nothing.
   */
  useEffect(() => {
    applyAppearance(settings)
    return watchSystemScheme(() => applyAppearance(settings))
  }, [settings])

  useEffect(() => {
    if (didInitialScroll.current || hasUserScrolled) return
    if (nowIndex === -1) return
    didInitialScroll.current = true
    virtualizer.scrollToIndex(nowIndex, { align: 'center' })
  }, [nowIndex, hasUserScrolled, virtualizer])

  const handleScroll = useCallback(() => {
    markUserScrolled()
    const first = virtualizer.getVirtualItems()[0]
    if (!first) return
    const day = timelineWindow.dayAt(first.index)
    if (day) setVisibleDayKey(day.dayKey)
  }, [markUserScrolled, setVisibleDayKey, virtualizer, timelineWindow])

  /**
   * Scrolls to the first block of a day and closes the calendar.
   *
   * Aligned to the start rather than the centre: jumping to a date means
   * "show me that day", and centring it would put the previous evening at the
   * top of the screen.
   */
  const goToDay = useCallback(
    (dayKey: number) => {
      const index = timelineWindow.indexOf({ dayKey, slotIndex: 0 })
      setShowCalendar(false)
      if (index === -1) return
      virtualizer.scrollToIndex(index, { align: 'start' })
    },
    [timelineWindow, virtualizer],
  )

  const goToNow = useCallback(() => {
    if (nowIndex === -1) return
    virtualizer.scrollToIndex(nowIndex, { align: 'center', behavior: 'smooth' })
  }, [nowIndex, virtualizer])

  /**
   * Collapsing a category is a synced write, not local state.
   *
   * It costs one small row per fold, which is nothing, and it means the
   * picker is laid out the same way on both devices. `useLiveQuery` re-renders
   * the picker from the row, so the fold is immediate regardless.
   */
  const handleToggleCategory = useCallback(
    (category: CategoryRow) => {
      void setCategoryCollapsed(category.id, category.isCollapsed === 0).then(scheduleSync)
    },
    [scheduleSync],
  )

  const headerDayKey = visibleDayKey ?? anchorDayKey

  useKeyboardShortcuts({
    onUndo: actions.undo,
    onRedo: actions.redo,
    onCopy: actions.copy,
    onCut: actions.cut,
    onPaste: () => actions.paste(),
    onErase: actions.eraseSelected,
    onToday: goToNow,
  })

  // --- Status text -----------------------------------------------------------

  let range: string | null = null
  let duration: string | null = null
  let filledWith: string | null = null

  if (selection) {
    const slots = selectionSlots(grid, selection)
    const first = slots[0]
    const last = slots[slots.length - 1]
    if (first && last) {
      const startMs = grid.startOfSlot(first.dayKey, first.slotIndex)
      const endMs = grid.startOfSlot(last.dayKey, last.slotIndex) + grid.slotDurationMs
      range = `${hourFormat.format(new Date(startMs))} – ${hourFormat.format(new Date(endMs))}`
      duration = formatDuration(selectionMinutes(grid, selection))

      const entry = runsByDay.get(first.dayKey)?.[first.slotIndex]?.entry
      if (slots.length > 1) filledWith = `${slots.length} blocks`
      else if (entry?.eventTypeId) filledWith = typeById.get(entry.eventTypeId)?.name ?? null
    }
  }

  const headerLabel = titleFormat.format(new Date(grid.startOfDay(headerDayKey) + 12 * 3_600_000))

  return (
    <div className="tl-screen">
      <header className="tl-titlebar safe-top safe-x">
        <h1 className="tl-title">{headerLabel}</h1>

        <div className="tl-actions">
          <button
            className="tl-icon"
            onClick={actions.undo}
            disabled={undoDepth === 0}
            aria-label="Undo"
          >
            ↺
          </button>
          <button
            className="tl-icon"
            onClick={actions.redo}
            disabled={redoDepth === 0}
            aria-label="Redo"
          >
            ↻
          </button>
          <button
            className="tl-icon"
            onClick={() => setShowMore(true)}
            aria-label={`More. Sync status: ${syncStatus}`}
          >
            <span aria-hidden="true">⋯</span>
            {syncStatus !== 'idle' && syncStatus !== 'syncing' ? (
              <span className={`tl-badge dot--${syncDot(syncStatus)}`} aria-hidden="true" />
            ) : null}
          </button>
          <button className="tl-today" onClick={goToNow} aria-label="Scroll to now">
            Now
          </button>
        </div>
      </header>

      <div
        ref={scrollRef}
        className="tl-scroll scroll-y"
        onScroll={handleScroll}
        role="grid"
        aria-label="Timeline"
        aria-rowcount={timelineWindow.totalRows}
        aria-multiselectable="true"
        style={
          {
            ['--tl-gutter' as string]: `${GUTTER_WIDTH}px`,
            ['--tl-divider' as string]: `${DIVIDER_HEIGHT}px`,
            ['--tl-row' as string]: `${heights.row}px`,
          } as React.CSSProperties
        }
        {...gestures}
      >
        <div className="tl-content" style={{ height: `${virtualizer.getTotalSize()}px` }}>
          {nowOffset !== null ? (
            <NowMarker
              offset={nowOffset}
              label={hourFormat.format(new Date(now))}
              style={settings.nowMarkerStyle}
            />
          ) : null}

          {virtualItems.map((item) => {
            const slot = timelineWindow.slotAt(item.index)
            if (!slot) return null

            const render = runsByDay.get(slot.dayKey)?.[slot.slotIndex] ?? null
            const entry = render?.entry ?? null
            const type = entry?.eventTypeId ? typeById.get(entry.eventTypeId) : undefined
            const fill = type?.colorHex ?? null

            const startsDay = timelineWindow.isFirstSlotOfDay(item.index)
            const startsHour = slot.slotIndex % perHour === 0
            const slotStart = grid.startOfSlot(slot.dayKey, slot.slotIndex)

            return (
              <div
                key={item.key}
                className="tl-item"
                role="row"
                aria-rowindex={item.index + 1}
                style={{ transform: `translateY(${item.start}px)`, height: `${item.size}px` }}
              >
                <BlockRow
                  dayKey={slot.dayKey}
                  slotIndex={slot.slotIndex}
                  startsDay={startsDay}
                  startsHour={startsHour}
                  hourLabel={hourFormat.format(new Date(slotStart))}
                  dayLabel={
                    startsDay
                      ? dayFormat.format(new Date(grid.startOfDay(slot.dayKey) + 12 * 3_600_000))
                      : ''
                  }
                  isoDate={dayKeyToISO(slot.dayKey)}
                  isNow={item.index === nowIndex}
                  isSelected={selectedKeys.has(slotKey(slot))}
                  fillColor={fill}
                  textColor={fill ? labelColorOn(fill) : ''}
                  label={
                    showLabels && type
                      ? blockLabel(
                          {
                            typeLabel: type.shortcutText || type.name,
                            objectName: entry?.eventObjectId
                              ? (objectNameById.get(entry.eventObjectId) ?? null)
                              : null,
                            comment: entry?.comment ?? null,
                          },
                          settings,
                        )
                      : ''
                  }
                  isRunStart={render?.isRunStart ?? false}
                  isRunEnd={render?.isRunEnd ?? false}
                  hasComment={entry?.comment !== null && entry?.comment !== undefined}
                  isPlanned={entry?.isPlanned === 1}
                  isJustMarked={justMarked.has(slotKey(slot))}
                  pattern={type?.pattern ?? null}
                />
              </div>
            )
          })}
        </div>
      </div>

      <StatusToolbar
        range={range}
        duration={duration}
        filledWith={filledWith}
        sticky={sticky}
        clipboardSize={clipboard?.items.length ?? 0}
        onPaste={() => actions.paste()}
        idleHint={null}
      />

      <div className="safe-bottom safe-x">
        <EventPicker
          onPick={actions.mark}
          onPickAndComment={(eventTypeId) => {
            const target = selection?.anchor
            actions.mark(eventTypeId)
            if (target) setDetail({ slot: target, focusComment: true })
          }}
          onErase={actions.eraseSelected}
          hasSelection={selection !== null}
          onToggleCategory={handleToggleCategory}
          onApplyStamper={actions.applyStamper}
        />
      </div>

      {menu ? (
        <BlockContextMenu
          x={menu.x}
          y={menu.y}
          hasClipboard={(clipboard?.items.length ?? 0) > 0}
          onEdit={() => {
            if (selection) setDetail({ slot: selection.anchor, focusComment: false })
            setMenu(null)
          }}
          onCopy={() => {
            actions.copy()
            setMenu(null)
          }}
          onCut={() => {
            actions.cut()
            setMenu(null)
          }}
          onPaste={() => {
            actions.paste()
            setMenu(null)
          }}
          onErase={() => {
            actions.eraseSelected()
            setMenu(null)
          }}
          onEraseDay={() => {
            actions.eraseDay(selection?.anchor.dayKey ?? headerDayKey)
            setMenu(null)
          }}
          onClose={() => setMenu(null)}
        />
      ) : null}

      {showMore ? (
        <MoreMenu
          syncStatus={syncStatus}
          onSync={() => {
            setShowMore(false)
            setShowSync(true)
          }}
          onBackup={() => {
            setShowMore(false)
            setShowBackup(true)
          }}
          onManage={() => {
            setShowMore(false)
            setShowManage(true)
          }}
          onStatistics={() => {
            setShowMore(false)
            setShowStats(true)
          }}
          onTrend={() => {
            setShowMore(false)
            setShowTrend(true)
          }}
          onCalendar={() => {
            setShowMore(false)
            setShowCalendar(true)
          }}
          onGoals={() => {
            setShowMore(false)
            setShowGoals(true)
          }}
          onStampers={() => {
            setShowMore(false)
            setShowStampers(true)
          }}
          onSettings={() => {
            setShowMore(false)
            setShowSettings(true)
          }}
          onClose={() => setShowMore(false)}
        />
      ) : null}

      {showManage ? (
        <ManageScreen onClose={() => setShowManage(false)} onChanged={scheduleSync} />
      ) : null}

      {showStats ? (
        <Suspense fallback={<ChartsLoading label="Statistics" />}>
          <StatisticsScreen
            grid={grid}
            todayDayKey={anchorDayKey}
            onClose={() => setShowStats(false)}
          />
        </Suspense>
      ) : null}

      {showTrend ? (
        <Suspense fallback={<ChartsLoading label="Trend" />}>
          <TrendScreen grid={grid} todayDayKey={anchorDayKey} onClose={() => setShowTrend(false)} />
        </Suspense>
      ) : null}

      {showCalendar ? (
        <Suspense fallback={<ChartsLoading label="Calendar" />}>
          <CalendarScreen
            todayDayKey={anchorDayKey}
            onPickDay={goToDay}
            onClose={() => setShowCalendar(false)}
            onChanged={scheduleSync}
          />
        </Suspense>
      ) : null}

      {showGoals ? (
        <Suspense fallback={<ChartsLoading label="Goals" />}>
          <GoalsScreen
            todayDayKey={anchorDayKey}
            onClose={() => setShowGoals(false)}
            onChanged={scheduleSync}
          />
        </Suspense>
      ) : null}

      {showStampers ? (
        <Suspense fallback={<ChartsLoading label="Stampers" />}>
          <StampersScreen onClose={() => setShowStampers(false)} onChanged={scheduleSync} />
        </Suspense>
      ) : null}

      {showSettings ? (
        <Suspense fallback={<ChartsLoading label="Settings" />}>
          <SettingsScreen
            onClose={() => setShowSettings(false)}
            onOpenSync={() => {
              setShowSettings(false)
              setShowSync(true)
            }}
            onOpenBackup={() => {
              setShowSettings(false)
              setShowBackup(true)
            }}
            onChanged={scheduleSync}
          />
        </Suspense>
      ) : null}

      {detail ? (
        <BlockDetail
          grid={grid}
          slot={detail.slot}
          focusComment={detail.focusComment}
          onClose={() => setDetail(null)}
          onChanged={scheduleSync}
        />
      ) : null}

      {showBackup ? (
        <BackupPanel onClose={() => setShowBackup(false)} onChanged={scheduleSync} />
      ) : null}

      {showSync ? (
        <SyncPanel
          status={syncStatus}
          lastSyncAt={lastSyncAt}
          onSyncNow={syncNow}
          onClose={() => setShowSync(false)}
        />
      ) : null}
    </div>
  )
}

/**
 * Placeholder while a chart screen's chunk downloads.
 *
 * Full-screen and opaque rather than a spinner over the timeline: the tap that
 * opened it was a navigation, and leaving the previous screen visible makes it
 * look as though nothing happened.
 */
function ChartsLoading({ label }: { label: string }) {
  return (
    <div className="tl-loading" role="status">
      Loading {label}…
    </div>
  )
}
